import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function TransactionBarChart({ transactions = [] }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [viewMode, setViewMode] = useState('netProfit');
  
  const casinoData = useMemo(() => {
    const casinos = {};
    
    transactions.forEach(transaction => {
      const casinoId = transaction.appId._id || transaction.appId;
      const casinoName = transaction.appId.name || 'Unknown';
      
      if (!casinos[casinoId]) {
        casinos[casinoId] = {
          name: casinoName,
          deposits: 0,
          withdrawals: 0
        };
      }
      
      const amount = Math.abs(transaction.amount);
      if (transaction.type === 'deposit') {
        casinos[casinoId].deposits += amount;
      } else {
        casinos[casinoId].withdrawals += amount;
      }
    });

    return Object.values(casinos)
      .filter(casino => casino.deposits > 0 || casino.withdrawals > 0)
      .map(casino => ({
        ...casino,
        netProfit: casino.withdrawals - casino.deposits
      }))
      .sort((a, b) => {
        switch(viewMode) {
          case 'deposits': return b.deposits - a.deposits;
          case 'withdrawals': return b.withdrawals - a.withdrawals;
          case 'all': return b.netProfit - a.netProfit;
          default: return b.netProfit - a.netProfit;
        }
      });
  }, [transactions, viewMode]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border rounded shadow">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: ${Math.abs(entry.value).toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="w-full" style={{ backgroundColor: '#FFFFFF' }}>
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <span className="text-lg sm:text-xl">Casino Performance</span>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded text-sm sm:text-base w-full sm:w-auto ${
                viewMode === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode('deposits')}
              className={`px-4 py-2 rounded text-sm sm:text-base w-full sm:w-auto ${
                viewMode === 'deposits' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Deposits
            </button>
            <button
              onClick={() => setViewMode('withdrawals')}
              className={`px-4 py-2 rounded text-sm sm:text-base w-full sm:w-auto ${
                viewMode === 'withdrawals' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Withdrawals
            </button>
            <button
              onClick={() => setViewMode('netProfit')}
              className={`px-4 py-2 rounded text-sm sm:text-base w-full sm:w-auto ${
                viewMode === 'netProfit' 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Net Profit
            </button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[800px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={casinoData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
              barSize={20}
              barGap={8}
            >
              <XAxis 
                type="number"
                tickFormatter={(value) => `$${value}`}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={55}
                tick={{ fontSize: 12 }}
                interval={0}
              />
              <Tooltip content={<CustomTooltip />} />
              {viewMode === 'all' ? (
                <>
                  <Legend />
                  <Bar dataKey="deposits" fill="#ef4444" name="Deposits" />
                  <Bar dataKey="withdrawals" fill="#16a34a" name="Withdrawals" />
                  <Bar dataKey="netProfit" fill="#6366f1" name="Net Profit" />
                </>
              ) : (
                <Bar 
                  dataKey={viewMode} 
                  fill={viewMode === 'deposits' ? '#ef4444' : viewMode === 'withdrawals' ? '#16a34a' : '#6366f1'}
                  name={viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} 
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}